import * as tus from 'tus-js-client';
import toQueryParams from '../../utils/toQueryParams';
import claimScoreApiServiceClient from '../client';
import { Paginated } from '../shared/types';
import { ClaimScoreApiVersion } from '../types';
import { getAuthorizationToken, getBaseURLWithVersion } from '../utils';
import {
  ClaimFiles,
  GetAllFilesPayload,
  GetSuperAdminClaimResultsPayload,
  GetSuperAdminClaimResultsResponse,
  SubmitBulkUploadFileParams,
  UpdateClaimFilesPayload,
} from './types';

const baseURL = getBaseURLWithVersion(ClaimScoreApiVersion.V2);
const BYTES_PER_KB = 1024;
const CHUNK_SIZE_MB = 50;

export const getSuperAdminClaimResults = async ({
  caseID,
  ...payload
}: GetSuperAdminClaimResultsPayload): Promise<GetSuperAdminClaimResultsResponse> => {
  const { data } = await claimScoreApiServiceClient.post(
    `/files/${caseID}/claims-results-super-admin`,
    payload,
    { responseType: 'blob' },
  );
  return data;
};

export const submitBulkUploadFile = async ({
  file,
  caseID,
  uploaderEmail,
  userId,
  containsHeader,
  headers,
  delimiter,
  escapeChar,
  onProgress,
  onSuccess,
  onError,
}: SubmitBulkUploadFileParams) => {
  const authToken = await getAuthorizationToken();
  const upload = new tus.Upload(file, {
    endpoint: `${baseURL}/bulk-upload/uploads/files`,
    chunkSize: CHUNK_SIZE_MB * BYTES_PER_KB * BYTES_PER_KB,
    metadata: {
      fileSize: file.size.toString(),
      fileName: file.name,
      fileType: file.type,
      caseID,
      uploaderEmail,
      delimiter,
      escapeChar,
      containsHeader,
      headers,
    },
    headers: {
      authorization: authToken,
      'x-case-id': caseID,
      'x-upload-session': userId,
    },
    onError(error) {
      if (onError) onError(error);
    },
    onProgress(bytesUploaded, bytesTotal) {
      const percentage = (bytesUploaded / bytesTotal) * 100;
      if (onProgress) onProgress(percentage);
    },
    onSuccess() {
      if (onSuccess) {
        onSuccess(upload);
      }
    },
  });

  upload.start();
  return upload;
};

export const getAllFiles = async ({
  pageNumber,
  pageSize,
  sortOrder,
  sortField,
  users,
  status,
  caseID,
}: GetAllFilesPayload): Promise<Paginated<ClaimFiles>> => {
  const { data } = await claimScoreApiServiceClient.get(
    `/files/${caseID}?${toQueryParams({
      pageNumber,
      pageSize,
      sortOrder,
      sortField,
      users,
      status,
    })}`,
  );

  return data;
};

export const deleteListOfFiles = async (caseID: string, fileIDs: string[]) => {
  const { data } = await claimScoreApiServiceClient.delete(`/files/${caseID}`, {
    data: { fileIDs },
  });
  return data;
};

export const updateFile = async ({
  caseID,
  fileID,
  fileStatus,
  enabledStatus,
}: UpdateClaimFilesPayload) => {
  const { data } = await claimScoreApiServiceClient.put(
    `/files/${caseID}/${fileID}`,
    {
      fileStatus,
      enabledStatus,
    },
  );
  return data;
};
